import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { actionOutcome, apiFetch } from '../api';
import usePolling from '../utils/usePolling';
import Modal from '../components/Modal';
import InfoTip from '../components/InfoTip';
import { useConfirm } from '../components/ConfirmDialog';
import { EMPTY_TEXT } from '../components/copy';
import { useCrumbLabel } from '../components/shell/crumbs';
import {
  Badge, CardSkeleton, CopyButton, EmptyState, Icon, Menu, MenuItem, MenuSeparator,
  PageHeader, ProgressBar, Skeleton, StatusBadge,
} from '../components/ui';
import { formatBytes, formatNumber } from '../components/charts';

// Short labels, the same ones the Users list uses for its Channels column.
const CHANNEL_LABEL = {
  telegram: 'Telegram', website: 'Web', teams: 'Teams', rossapp: 'App', 'rossapp-pen': 'App (pentest)',
  voice: 'Phone', whatsapp: 'WhatsApp',
};
const channelLabel = (c) => CHANNEL_LABEL[c] || (c.charAt(0).toUpperCase() + c.slice(1));

// 2-state lifecycle, read the way the Users list reads it: the backend's `state` first,
// the older `is_paired` boolean as the fallback.
const pairState = (user) => user.state || (user.is_paired !== false ? 'paired' : 'not_paired');

const when = (iso, fallback) => (iso ? new Date(iso).toLocaleString() : fallback);

// Pod stdout carries the runtime's ANSI colour codes, which a plain <div> prints as
// `[2m…[0m` around every field. LogViewer strips them the same way.
// eslint-disable-next-line no-control-regex
const stripAnsi = (s) => (typeof s === 'string' ? s.replace(/\x1b\[[0-9;]*[A-Za-z]/g, '') : s);

// A null resource field uses the provider's protected platform default.
const capacity = (value, unit, fallback = 'Platform default') => (value != null
  ? <span className="num">{value} {unit}</span>
  : <span className="text-muted">{fallback}</span>);

// Shown under the page title while a lifecycle request is in flight. Two of the four
// start from the actions menu, which closes on select, so without this line nothing on
// the page would say that anything is happening.
const PENDING_LABEL = {
  stop: 'Stopping agent…',
  start: 'Starting agent…',
  reset: 'Clearing chat history…',
};

const ordinal = (n) => {
  const t = n % 100;
  if (t >= 11 && t <= 13) return `${n}th`;
  return `${n}${({ 1: 'st', 2: 'nd', 3: 'rd' })[n % 10] || 'th'}`;
};

// The user's workspace on disk, as the control-plane last measured it
// (GET /api/users/:id/storage, backend/services/workspace_usage.py).
function WorkspaceStorage({ storage }) {
  if (!storage) {
    return <Skeleton height={14} />;
  }
  if (!storage.has_workspace) {
    return <p className="text-faint m-0">No workspace yet: it is created at the user's first message.</p>;
  }
  if (!storage.measured) {
    return (
      <p className="text-faint m-0">
        Not measured yet. The control-plane measures new workspaces within
        {` ${Math.round((storage.interval_s || 900) / 60)} minutes.`}
      </p>
    );
  }
  const cap = storage.cap_bytes;
  return (
    <dl className="kv">
      <dt>Used</dt>
      <dd className="row gap-2">
        <span className="num fw-500">{formatBytes(storage.bytes_used)}</span>
        {storage.status === 'partial' && <Badge tone="warn">at least</Badge>}
        {storage.status === 'missing' && <Badge tone="warn">folder missing</Badge>}
      </dd>
      <dt>Share</dt>
      <dd>
        <span className="num">{storage.share_pct}%</span>
        <span className="text-muted">
          {` of all workspaces · ${ordinal(storage.rank)} largest of ${formatNumber(storage.measured_users)}`}
        </span>
      </dd>
      <dt>Cap</dt>
      <dd>
        {cap ? (
          <div className="usage-row compact">
            <ProgressBar value={storage.bytes_used} max={cap}
                         label={`Workspace storage used ${formatBytes(storage.bytes_used)} of ${formatBytes(cap)}`} />
            <span className="usage-value num">{formatBytes(cap)}</span>
          </div>
        ) : <span className="text-muted">None set</span>}
      </dd>
      <dt>Files</dt>
      <dd className="num">{formatNumber(storage.files)}</dd>
      <dt>Measured</dt>
      <dd>{when(storage.measured_at, 'Never')}</dd>
    </dl>
  );
}

// A channel address: copyable when set, "Not paired" when it is not.
function IdValue({ value, label }) {
  if (value == null || value === '') return <span className="text-faint">Not paired</span>;
  return (
    <span className="row gap-1">
      <code>{value}</code>
      <CopyButton value={value} label={label} />
    </span>
  );
}

function UserDetail() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState(null);
  // False until the first status probe settles, so the live check can tell "still
  // asking" apart from "the probe failed".
  const [statusChecked, setStatusChecked] = useState(false);
  // Workspace disk usage; null until it loads, and stays null if it cannot.
  const [storage, setStorage] = useState(null);
  const [logs, setLogs] = useState('');
  const [loading, setLoading] = useState(true);
  const [showLogs, setShowLogs] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  // `deleting` ∈ null | 'hard' | 'soft' — drives the gray-button +
  // spinner state on whichever delete button was clicked, and disables
  // all three modal buttons (incl. Cancel) until the request settles.
  const [deleting, setDeleting] = useState(null);
  // The lifecycle request in flight: null | 'stop' | 'start' | 'reset'.
  // Disables the agent controls, so one click cannot be sent twice.
  const [pending, setPending] = useState(null);
  const [confirm, confirmDialog] = useConfirm();
  // The analyst role reads this page and changes nothing: no Edit user, no agent controls
  // and no More actions menu. The backend refuses them anyway (api/analyst_guard.py).
  const readOnly = localStorage.getItem('admin_role') === 'analyst';

  useCrumbLabel(user?.username);
  // User ids are numbers. Anything else in the URL (an analyst's /users/create lands here,
  // since that route is an admin's) is a user that does not exist, not a request to make.
  const validId = /^\d+$/.test(userId || '');

  const fetchUser = useCallback(async () => {
    if (!validId) {
      setLoading(false);
      return;
    }
    try {
      const response = await apiFetch(`/api/users/${userId}`);
      const data = await response.json();
      // An error answers with {detail}. Stored as the user, that rendered a page with no
      // name on it, and the "not found" state below could never show.
      if (response.ok) setUser(data);
      else if (response.status !== 404) toast.error('Failed to fetch user details');
      setLoading(false);
    } catch (error) {
      console.error('Error fetching user:', error);
      toast.error('Failed to fetch user details');
      setLoading(false);
    }
  }, [userId, validId]);

  // Container status. Polled every 10s by the effect below.
  //
  // This used to GET /api/containers/${userId}/status, which DOES NOT EXIST — the
  // /api/containers router only defines /logs, /machine-logs, /stop, /start and
  // /reset-session. So every open user-detail page 404'd every 10 seconds and the
  // status panel never populated. The real route lives under a different prefix.
  //
  // It also expected the old Docker-era envelope ({status:'success', stats:{…}} with
  // cpu_percent / memory_usage_mb / started_at). The k8s endpoint returns
  // {container_name, runtime_type, apps, tools, image_tag, status:{Status, Running}},
  // so the response is normalised here onto the shape the JSX already renders. The
  // per-container CPU/memory figures are gone with Docker and simply stay undefined,
  // which the render guards on.
  const fetchStatus = useCallback(async () => {
    if (!validId) return;
    try {
      const response = await apiFetch(`/api/openpaw/${userId}/status`);
      if (!response.ok) return;
      const data = await response.json();
      setStatus({
        status: data?.status?.Status ?? 'unknown',
        image: data?.image_tag ?? null,
      });
    } catch (error) {
      console.error('Error fetching status:', error);
    } finally {
      setStatusChecked(true);
    }
  }, [userId, validId]);

  useEffect(() => { fetchUser(); }, [fetchUser]);
  // Once per visit: the figure moves at most every measuring interval.
  useEffect(() => {
    if (!validId) return undefined;
    let live = true;
    apiFetch(`/api/users/${userId}/storage`)
      .then(r => (r.ok ? r.json() : null))
      .then(body => { if (live) setStorage(body ? { ...body, loaded: true } : { failed: true }); })
      .catch(() => { if (live) setStorage({ failed: true }); });
    return () => { live = false; };
  }, [userId, validId]);
  // Status now and every 10s while the tab is visible (utils/usePolling).
  usePolling(fetchStatus, 10000, [fetchStatus]);

  const fetchLogs = async () => {
    setLogsLoading(true);
    try {
      const response = await apiFetch(`/api/containers/${userId}/logs?tail=200`);
      const data = await response.json();
      // The route answers {logs, container, source} and has never sent `status`, so
      // requiring `status === 'success'` on its own meant this panel could not open.
      if (data.status === 'success' || (response.ok && typeof data.logs === 'string')) {
        setLogs(stripAnsi(data.logs));
        setShowLogs(true);
      } else {
        toast.error('Failed to fetch logs');
      }
    } catch (error) {
      console.error('Error fetching logs:', error);
      toast.error('Failed to fetch logs');
    } finally {
      setLogsLoading(false);
    }
  };

  const handleContainerAction = async (action) => {
    setPending(action);
    try {
      const response = await apiFetch(`/api/containers/${userId}/${action}`, {
        method: 'POST',
      });
      // Not `status === 'success'`: the control-plane never sends it, so every successful
      // Start and Stop showed as failed and the page was not refreshed (api.js).
      const { ok, message } = await actionOutcome(response, `Agent ${action} failed`);
      if (ok) {
        toast.success(message || `Agent ${action} successful`);
      } else {
        toast.error(message);
      }
      // Either way: a refusal means another operation is moving the agent.
      fetchUser();
      fetchStatus();
    } catch (error) {
      console.error(`Error ${action} agent:`, error);
      toast.error(`Failed to ${action} agent`);
    } finally {
      setPending(null);
    }
  };

  const handleResetSession = async () => {
    if (!await confirm({
      title: 'Clear this user\u2019s chat history?',
      body: (
        <p>
          The conversation is erased and the agent restarts. Their workspace files,
          memory and instructions are untouched.
        </p>
      ),
      confirmLabel: 'Clear history',
    })) return;
    setPending('reset');
    try {
      const response = await apiFetch(`/api/containers/${userId}/reset-session`, { method: 'POST' });
      const { ok, body, message } = await actionOutcome(response, 'Failed to reset session');
      if (ok) {
        toast.success(`Session reset, cleared ${body.sessions_cleared ?? 0} session(s)`);
      } else {
        toast.error(message);
      }
      fetchUser();
      fetchStatus();
    } catch (error) {
      toast.error('Failed to reset session');
    } finally {
      setPending(null);
    }
  };

  const handleDelete = async (deleteData) => {
    setDeleting(deleteData ? 'hard' : 'soft');
    try {
      const response = await apiFetch(`/api/users/${userId}?delete_data=${deleteData}`, {
        method: 'DELETE',
      });

      const { ok, message } = await actionOutcome(response, 'Failed to delete user');
      if (ok) {
        toast.success(deleteData
          ? 'User and all data permanently deleted'
          : 'User deleted, record kept');
        navigate('/users');
      } else {
        toast.error(message);
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user');
    } finally {
      setDeleting(null);
      setShowDeleteModal(false);
    }
  };

  const agentState = user?.container_status;
  const agentUp = agentState === 'running' || agentState === 'hibernated';
  const busy = pending !== null;

  const header = (
    <PageHeader
      // The way back on phones, where the top bar shows only the last crumb.
      eyebrow={<Link to="/users">Users</Link>}
      description={readOnly
        ? 'Everything recorded for this user.'
        : 'Everything recorded for this user, and the controls for their agent.'}
      meta={(
        <div role="status">
          {pending && (
            <p className="row gap-2 mt-2 text-sm text-muted">
              <span className="spinner-sm" aria-hidden="true" />
              {PENDING_LABEL[pending]}
            </p>
          )}
        </div>
      )}
      actions={user ? (
        <>
          <Link to={`/users/${userId}/logs`} className="btn btn-secondary">
            <Icon name="terminal" /> View logs
          </Link>
          {!readOnly && (
            <>
              <Link to={`/users/${userId}/edit`} className="btn btn-secondary">
                <Icon name="edit" /> Edit user
              </Link>
              {agentUp && (
                <button type="button" className="btn btn-secondary" disabled={busy}
                        onClick={() => handleContainerAction('stop')}>
                  {pending === 'stop' ? <span className="spinner-sm" /> : <Icon name="stop" />} Stop agent
                </button>
              )}
              {/* 'pending' has never run: the user's first message starts it, and so does this. */}
              {(agentState === 'stopped' || agentState === 'pending') && (
                <button type="button" className="btn btn-success" disabled={busy}
                        onClick={() => handleContainerAction('start')}>
                  {pending === 'start' ? <span className="spinner-sm" /> : <Icon name="play" />} Start agent
                </button>
              )}
              {/* Clear-history is the less common agent control, and Delete sits under a
                  separator at the bottom, away from it. There is no restart item: the
                  containers API has stop and start, which together are one. */}
              <Menu label="More actions" icon="moreH" buttonClassName="btn btn-secondary btn-icon"
                    disabled={busy}>
                {agentUp && (
                  <>
                    <MenuItem icon="history" onSelect={handleResetSession}>Clear chat history</MenuItem>
                    <MenuSeparator />
                  </>
                )}
                <MenuItem icon="trash" danger onSelect={() => setShowDeleteModal(true)}>
                  Delete user
                </MenuItem>
              </Menu>
            </>
          )}
        </>
      ) : null}
    >
      <h1 className="page-title">
        {user ? user.username : `User ${userId}`}
        {user && <StatusBadge status={user.container_status} />}
        {user?.tier_name && <Badge tone="accent" size="lg">{user.tier_name}</Badge>}
        {loading && <Skeleton width={96} height={22} radius="var(--r-full)" />}
      </h1>
    </PageHeader>
  );

  if (loading) {
    return (
      <div>
        {header}
        <CardSkeleton lines={6} />
        <CardSkeleton lines={3} />
      </div>
    );
  }

  if (!user) {
    return (
      <div>
        {header}
        <div className="card">
          <EmptyState
            icon="users"
            title="User not found"
            action={<Link to="/users" className="btn btn-secondary">Back to users</Link>}
          >
            No user with ID {userId} could be loaded. It may have been deleted.
          </EmptyState>
        </div>
      </div>
    );
  }

  const channels = user.channels || [];
  const paired = pairState(user) === 'paired';

  return (
    <div>
      {header}

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Overview</h2>
        </div>
        <div className="grid-2">
          <section className="card-inset">
            <h3 className="overline mb-3">Account</h3>
            <dl className="kv">
              <dt>Username</dt>
              <dd className="fw-500">{user.username}</dd>
              <dt>User ID</dt>
              <dd className="mono">{user.id}</dd>
              <dt>Email</dt>
              <dd>{user.email || <span className="text-faint">Not set</span>}</dd>
              <dt>Status</dt>
              <dd>
                <div className="row-wrap gap-3 text-sm fw-500">
                  <span className={`row gap-1 ${user.is_active ? 'text-ok' : 'text-danger'}`}>
                    <span className="dot" aria-hidden="true" />{user.is_active ? 'Active' : 'Inactive'}
                  </span>
                  <span className={`row gap-1 ${paired ? 'text-muted' : 'text-warn'}`}>
                    <span className="dot" aria-hidden="true" />{paired ? 'Paired' : 'Not paired'}
                  </span>
                </div>
              </dd>
              <dt>Tier</dt>
              <dd>
                {user.tier_name
                  ? <Badge tone="accent">{user.tier_name}</Badge>
                  : <span className="text-faint">No tier</span>}
              </dd>
              <dt>Source</dt>
              <dd>
                <div className="row-wrap gap-2">
                  <Badge tone={user.source === 'stripe' ? 'violet' : 'neutral'}>{user.source || 'manual'}</Badge>
                  {/* Which coupon brought this user in. 'legacy' is the backfill stamp for
                      redemptions that predate migration 009. Not for an analyst, whom the
                      backend does not send it. */}
                  {!readOnly && user.source === 'coupon' && user.activation_coupon_code && (
                    <span className="mono text-sm text-muted">{user.activation_coupon_code}</span>
                  )}
                </div>
              </dd>
            </dl>
          </section>

          <section className="card-inset">
            <h3 className="overline mb-3">Channels</h3>
            <dl className="kv">
              <dt>Connected</dt>
              <dd>
                {channels.length ? (
                  <div className="row-wrap gap-1">
                    {channels.map((c) => <Badge key={c} tone="outline">{channelLabel(c)}</Badge>)}
                  </div>
                ) : <span className="text-faint">None</span>}
              </dd>
              <dt>Telegram ID</dt>
              <dd><IdValue value={user.telegram_id} label="Copy Telegram ID" /></dd>
              {/* Beside the Telegram id, because they are the same kind of thing: the channel's
                * address for this user. Read from channel_identities, so it is what a website
                * message actually arrives addressed to. */}
              <dt>RossHub member ID</dt>
              <dd><IdValue value={user.website_id} label="Copy RossHub member ID" /></dd>
            </dl>
          </section>

          <section className="card-inset">
            <h3 className="overline mb-3">Activity</h3>
            <dl className="kv">
              <dt>Created</dt>
              <dd>{when(user.created_at, 'Unknown')}</dd>
              <dt>Last active</dt>
              <dd>{when(user.last_active_at, 'Never')}</dd>
              {/* Coupon-driven expiry only: Stripe and manual users have no snapshot expiry.
                  Once the daily lifecycle sweep has stopped the user the date is struck
                  through and the stop date follows it. */}
              {user.source === 'coupon' && user.coupon_expires_at && (
                <>
                  <dt>Coupon expires</dt>
                  <dd>
                    <span className={user.coupon_stopped_at ? 'strike' : undefined}>
                      {when(user.coupon_expires_at)}
                    </span>
                    {user.coupon_stopped_at && (
                      <span className="text-muted">{` · stopped ${when(user.coupon_stopped_at)}`}</span>
                    )}
                  </dd>
                </>
              )}
            </dl>
          </section>

          <section className="card-inset">
            <h3 className="overline mb-3">Resources</h3>
            <dl className="kv">
              <dt>CPU reserved</dt>
              <dd>{capacity(user.cpu_reserved, 'vCPU')}</dd>
              <dt>CPU limit</dt>
              <dd>{capacity(user.cpu_limit, 'vCPU', 'Protected ceiling, 0.5 vCPU minimum')}</dd>
              <dt>Browser engine</dt>
              <dd>{user.browser_engine === 'chromium' ? 'Chromium' : 'Lightpanda'}</dd>
              <dt>Memory reserved</dt>
              <dd>{capacity(user.memory_reserved_gb, 'GB')}</dd>
              <dt>Memory limit</dt>
              <dd>{capacity(user.memory_limit_gb, 'GB')}</dd>
            </dl>
          </section>

          <section className="card-inset">
            <h3 className="overline mb-3">
              Workspace storage
              <InfoTip label="About workspace storage" width={320}>
                The size of the user's folder on the shared filesystem, measured by the
                control-plane: within 15 minutes of activity, otherwise once a day. The
                share is of every measured workspace on the platform. A cap is shown when
                the user or their tier has one; it is not enforced yet.
              </InfoTip>
            </h3>
            {storage?.failed
              ? <p className="text-faint m-0">Could not load the measurement.</p>
              : <WorkspaceStorage storage={storage} />}
          </section>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">
            Agent
            <InfoTip label="About the agent status" width={320}>
              Status is what the platform has recorded for this agent. The live check asks
              the cluster every 10 seconds. A hibernated agent has no running pod, so its
              live check reads stopped.
            </InfoTip>
          </h2>
          {/* The live check re-polls every 10s; saying so explains a value that changes
              while the page is open. */}
          {status && <Badge tone="ok" dot pulse>Live</Badge>}
        </div>
        {agentState === 'hibernated' && (
          <div className="notice notice-info">
            <Icon name="moon" />
            <div className="notice-body">
              <span className="notice-title">Hibernated.</span>{' '}
              Online with compute paused. It wakes automatically on activity.
            </div>
          </div>
        )}
        <div className="info-grid mb-0">
          <div className="info-item">
            <div className="info-label">Status</div>
            <div className="info-value"><StatusBadge status={user.container_status} /></div>
          </div>
          <div className="info-item">
            <div className="info-label">Live check</div>
            <div className="info-value">
              {status ? <StatusBadge status={status.status} />
                : statusChecked ? <span className="text-faint">Unavailable</span>
                  : <Skeleton width={72} height={22} radius="var(--r-full)" />}
            </div>
          </div>
          <div className="info-item">
            <div className="info-label">Image</div>
            <div className="info-value mono">{status?.image || user.image || 'Platform default'}</div>
          </div>
          {status?.cpu_percent !== undefined && (
            <>
              <div className="info-item">
                <div className="info-label">CPU usage</div>
                <div className="info-value num">{status.cpu_percent}%</div>
              </div>
              <div className="info-item">
                <div className="info-label">Memory usage</div>
                <div className="info-value num">
                  {status.memory_usage_mb} MB ({status.memory_percent}%)
                </div>
              </div>
            </>
          )}
          {status?.started_at && (
            <div className="info-item">
              <div className="info-label">Started</div>
              <div className="info-value">{new Date(status.started_at).toLocaleString()}</div>
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">
            Recent logs
            <InfoTip label="About these agent logs" width={320}>
              The last 200 lines. A healthy agent logs nothing at WARN or above,
              so an empty view is the normal case rather than a gap.
            </InfoTip>
          </h2>
          <div className="card-actions">
            {showLogs && (
              <>
                <button type="button" className="btn btn-secondary btn-sm" onClick={fetchLogs}
                        disabled={logsLoading}>
                  {logsLoading ? <span className="spinner-sm" /> : <Icon name="refresh" />} Refresh
                </button>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowLogs(false)}>
                  Hide logs
                </button>
              </>
            )}
            <Link to={`/users/${userId}/logs`} className="btn btn-ghost btn-sm">
              Full logs <Icon name="arrowRight" />
            </Link>
          </div>
        </div>
        {showLogs ? (
          <div className="logs-container">
            {logs || EMPTY_TEXT}
          </div>
        ) : (
          <EmptyState
            icon="terminal"
            title="Nothing loaded yet"
            action={(
              <button type="button" className="btn btn-secondary" onClick={fetchLogs} disabled={logsLoading}>
                {logsLoading && <span className="spinner-sm" />} Load recent logs
              </button>
            )}
          >
            Fetch the last 200 lines of this agent&apos;s output.
          </EmptyState>
        )}
      </div>

      {/* Restructured, not restyled. The two options were described in a paragraph
          BELOW the buttons, so you had to read past the controls to learn what they
          did; and Soft delete used `btn-secondary`, the same style as Cancel, so the
          only real choice on the dialog looked like the way out of it. Description
          first, then Cancel / Soft / Hard left to right by increasing consequence.

          Hard delete keeps a second confirmation, because it is irreversible and its
          button sits beside one that is not — but through the app's own dialog rather
          than a native `window.confirm` stacked on top of this one. */}
      <Modal open={showDeleteModal} onClose={() => { if (!deleting) setShowDeleteModal(false); }}
             title={user ? `Delete user ${user.username}?` : ''}>
        {/* Both options remove the agent and its workspace (services/users.py
            soft_delete_user runs the same rmtree as the hard delete). What differs is the
            database record. This dialog used to say soft delete preserved the data. */}
        <p>Both options remove the agent and its workspace files. They differ in what is kept.</p>
        <dl className="stack gap-3 mt-4">
          <div>
            <dt className="fw-600 text-strong">Soft delete</dt>
            <dd className="text-muted">
              Keeps the user&apos;s record, logs and usage history, marked deleted, and frees
              their username. The workspace cannot be restored from here.
            </dd>
          </div>
          <div>
            <dt className="fw-600 text-strong">Hard delete</dt>
            <dd className="text-muted">
              Also removes the record. Usage is kept only as daily totals. Cannot be undone.
            </dd>
          </div>
        </dl>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            disabled={!!deleting}
            onClick={() => setShowDeleteModal(false)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!!deleting}
            onClick={() => handleDelete(false)}
          >
            {deleting === 'soft' ? <><span className="spinner-sm" /> Deleting…</> : 'Soft delete'}
          </button>
          <button
            type="button"
            className={deleting === 'hard' ? 'btn btn-secondary' : 'btn btn-danger'}
            disabled={!!deleting}
            onClick={async () => {
              if (!await confirm({
                title: 'Permanently delete all data for this user?',
                body: <p>The workspace and config are erased. This cannot be undone.</p>,
                confirmLabel: 'Erase permanently',
              })) return;
              handleDelete(true);
            }}
          >
            {deleting === 'hard' ? <><span className="spinner-sm" /> Deleting…</> : 'Hard delete'}
          </button>
        </div>
      </Modal>

      {confirmDialog}
    </div>
  );
}

export default UserDetail;
